// Utility functions converted from Vue utils
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") {
    return Object.values(value).every(
      (val) =>
        val === null ||
        val === undefined ||
        val === "" ||
        val === 0 ||
        val === false ||
        (Array.isArray(val) && val.length === 0) ||
        (typeof val === "object" && Object.keys(val).length === 0),
    );
  }
  return false;
};

export const getParamIfExists = (searchParams: any, key: string): string | undefined => {
  const value = searchParams.get(key);
  return value ? value : undefined;
};

export const formatDate = (date: string | Date, format: string = "DD/MM/YYYY"): string => {
  if (!date) return "";
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();

  switch (format) {
    case "DD/MM/YYYY":
      return `${day}/${month}/${year}`;
    case "YYYY-MM-DD":
      return `${year}-${month}-${day}`;
    default:
      return d.toLocaleDateString();
  }
};

export const generateBrowserId = (): string => {
  return "browser_" + Math.random().toString(36).substr(2, 9) + Date.now();
};

export const getBrowserId = (): string => {
  let browserId = localStorage.getItem("browser_id");
  if (!browserId) {
    browserId = generateBrowserId();
    localStorage.setItem("browser_id", browserId);
  }
  return browserId;
};

export const formatCurrency = (number: number, locale = 'vi-VN', options?: object) => {
    return number ? number.toLocaleString(locale, options) : '0';
};

export const formatAmountCurrency = (amount: any, currency: any) => {
    if (isEmpty(currency)) {
        return formatCurrency(amount);
    }

    return `${formatCurrency(amount)}${currency?.symbol ?? 'VNĐ'}`;
}

export const cloneDeep = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map(cloneDeep);
    } else if (obj && typeof obj === 'object') {
        const cloned: any = {};
        const keys = Object.keys(obj);
        for (let i = 0, l = keys.length; i < l; i++) {
            const key = keys[i];
            cloned[key] = cloneDeep(obj[key]);
        }
        return cloned;
    } else {
        return obj;
    }
};

export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const throttle = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
};

export const parseStorageValue = (value: string | null): any => {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const trimStringProperties = (obj: any): any => {
  if (typeof obj === "string") {
    return obj.trim();
  }

  if (typeof obj === "object") {
    const trimmedObj: any = {};

    for (const key in obj) {
      // eslint-disable-next-line no-prototype-builtins
      if (!obj.hasOwnProperty(key)) {
        continue;
      }
      trimmedObj[key] = trimStringProperties(obj[key]);
    }

    return trimmedObj;
  }

  return obj;
};

export const generateUUID = (): string => {
  // Simple UUID v4 generation
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Số ngày bảo hành còn lại tính từ chuỗi ngày dd/mm/yyyy (port từ app baohanh-barcode cũ).
 * Trả về 0 nếu đã hết hạn hoặc chuỗi không hợp lệ.
 */
export const daysDiffWarrantyEnd = (dateString: string): number => {
  if (!dateString) return 0;

  const [day, month, year] = dateString.split("/");
  if (!day || !month || !year) return 0;

  const endDate = new Date(Number(year), Number(month) - 1, Number(day));
  if (isNaN(endDate.getTime())) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const timeDiff = endDate.getTime() - today.getTime();
  if (timeDiff <= 0) return 0;

  return Math.ceil(timeDiff / (1000 * 3600 * 24));
};

export function convertIsoToDate(isoString: string): string {
  // Chuẩn hóa chuỗi ISO để đảm bảo tương thích với Safari
  const fixedIsoString = isoString.replace(/-/g, '/').replace(/T/, ' ').replace(/Z/, '');
  const date = new Date(fixedIsoString);

  // Kiểm tra nếu trình duyệt không xử lý được định dạng
  if (isNaN(date.getTime())) {
    // Thử chuyển chuỗi gốc thành Date bằng cách sử dụng định dạng chuẩn
    const fallbackDate = new Date(isoString);
    if (!isNaN(fallbackDate.getTime())) {
      return formatDate(fallbackDate);
    }
    throw new Error('Invalid ISO date string');
  }

  return formatDate(date);
}

/**
 * Reverse geocoding: Lấy địa chỉ từ latitude và longitude
 * Sử dụng Nominatim OpenStreetMap API (miễn phí, không cần API key)
 */
export const reverseGeocode = async (lat: number, lon: number): Promise<string | null> => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`;

    const response = await fetch(url, {
      headers: {
        'User-Agent': 'QRX-Landing-Page/1.0'
      }
    });

    if (!response.ok) {
      console.warn('Reverse geocoding failed:', response.statusText);
      return null;
    }

    const data = await response.json();

    if (data.error) {
      console.warn('Reverse geocoding error:', data.error);
      return null;
    }

    // Trích xuất thông tin địa chỉ
    const address = data.address || {};
    const parts = [
      address.road || address.street,
      address.suburb || address.neighbourhood,
      address.city || address.town || address.village,
      address.state,
      address.country
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : data.display_name || null;
  } catch (error) {
    console.warn('Error in reverse geocoding:', error);
    return null;
  }
};
